'use client';

import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Slot as SlotPrimitive } from 'radix-ui';
import { tv } from 'tailwind-variants';

import { faBars } from '@icons/classic/regular';
import { useIsMobile } from '../hooks';
import { Overline } from '../typography';
import { Button } from './Button';
import { Input } from './Input';
import { Separator } from './Separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './Sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './Tooltip';

import type { VariantProps } from 'tailwind-variants';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '16rem';
const SIDEBAR_WIDTH_ICON = '4rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = use(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

const sidebar = tv({
  slots: {
    wrapper: `
      group/sidebar-wrapper
      has-data-[variant=inset]:bg-sidebar
      flex
      min-h-svh
      w-full
    `,
    root: `
      bg-sidebar
      p-0
      [&>button]:hidden
    `,
    gap: `
      relative
      w-(--sidebar-width)
      bg-transparent
      transition-[width]
      duration-200
      ease-linear
      group-data-[collapsible=offcanvas]:w-0
      group-data-[side=right]:rotate-180
    `,
    container: `
      fixed
      inset-y-0
      z-10
      hidden
      h-svh
      w-(--sidebar-width)
      transition-[left,right,width]
      duration-200
      ease-linear
      md:flex
    `,
    content: `
      flex
      min-h-0
      flex-1
      flex-col
      gap-5
      overflow-y-auto
      overflow-x-hidden
      py-3
      px-2.5
      group-data-[collapsible=icon]:overflow-hidden
      group-data-[collapsible=icon]:gap-18
    `,
    header: `
      flex
      items-center
      justify-between
      px-4
      pt-6
      pb-4
    `,
    footer: `
      flex
      flex-col
      items-center
      justify-between
      px-3
      py-3
    `,
    group: `
      relative
      flex
      w-full
      min-w-0
      flex-col
      gap-2
      py-2
      group-data-[collapsible=icon]:after:content-['']
      group-data-[collapsible=icon]:after:absolute
      group-data-[collapsible=icon]:after:-bottom-6
      group-data-[collapsible=icon]:after:left-1/2
      group-data-[collapsible=icon]:after:-translate-x-1/2
      group-data-[collapsible=icon]:after:w-8
      group-data-[collapsible=icon]:after:h-px
      group-data-[collapsible=icon]:after:bg-border-secondary
      group-data-[collapsible=icon]:last:after:hidden
    `,
    groupAction: `
      text-sidebar-foreground
      ring-sidebar-ring
      hover:bg-sidebar-accent
      hover:text-sidebar-accent-foreground
      absolute
      top-3.5
      right-3
      flex
      aspect-square
      w-5
      items-center
      justify-center
      rounded-md
      p-0
      outline-hidden
      transition-transform
      group-data-[collapsible=icon]:hidden
      after:absolute
      after:-inset-2
      focus-visible:ring-2 md:after:hidden
      [&>svg]:size-4
      [&>svg]:shrink-0
    `,
    groupContent: `
      w-full
    `,
    groupLabel: `
      ring-sidebar-ring
      text-content-secondary
      flex
      h-8
      shrink-0
      items-center
      px-2
      font-medium
      uppercase
      transition-[margin,opacity]
      duration-200
      ease-linear
      group-data-[collapsible=icon]:-mt-8
      group-data-[collapsible=icon]:opacity-0
      group-data-[collapsible=icon]:h-0
      focus-visible:ring-2
      [&>svg]:size-4
      [&>svg]:shrink-0
    `,
    input: `
      bg-background
      h-8
      w-full
      shadow-none
    `,
    inset: `
      bg-background
      relative
      flex
      w-full
      flex-1
      flex-col
      md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2
    `,
    menu: `
      flex
      w-full
      min-w-0
      flex-col
      gap-1
    `,
    menuAction: `
      text-sidebar-foreground
      ring-sidebar-ring
      hover:bg-sidebar-accent
      hover:text-sidebar-accent-foreground
      peer-hover/menu-button:text-sidebar-accent-foreground
      absolute
      top-1.5
      right-1
      flex
      aspect-square
      w-5
      items-center
      justify-center
      rounded-md
      p-0
      outline-hidden
      transition-transform
      group-data-[collapsible=icon]:hidden
      peer-data-[size=default]/menu-button:top-1.5
      peer-data-[size=lg]/menu-button:top-2.5
      peer-data-[size=sm]/menu-button:top-1
      after:absolute
      after:-inset-2
      focus-visible:ring-2
      md:after:hidden
      [&>svg]:size-4
      [&>svg]:shrink-0
    `,
    menuBadge: `
      text-sidebar-foreground peer-hover/menu-button:text-sidebar-accent-foreground
      peer-data-[active=true]/menu-button:text-sidebar-accent-foreground
      pointer-events-none
      absolute
      right-1
      flex
      h-5
      min-w-5 items-center
      justify-center
      rounded-md
      px-1
      text-xs
      font-medium
      tabular-nums
      select-none
      group-data-[collapsible=icon]:hidden
      peer-data-[size=default]/menu-button:top-1.5
      peer-data-[size=lg]/menu-button:top-2.5
      peer-data-[size=sm]/menu-button:top-1
    `,
    menuItem: `
      group/menu-item
      relative
      w-full
      list-none
      whitespace-nowrap
    `,
    menuSub: `
      border-sidebar-border
      mx-3.5
      flex
      min-w-0
      translate-x-px
      flex-col
      gap-1
      border-l
      px-2.5
      py-0.5
      group-data-[collapsible=icon]:hidden
    `,
    menuSubItem: `
      group/menu-sub-item relative
    `,
    sidebar: `
      bg-background-secondary
      group-data-[variant=floating]:border-sidebar-border
      flex
      h-full
      w-full
      flex-col
      group-data-[variant=floating]:rounded-lg
      group-data-[variant=floating]:border
      group-data-[variant=floating]:shadow-sm
    `,
    sidebarRail: `
      hover:after:bg-sidebar-border
      hover:group-data-[collapsible=offcanvas]:bg-sidebar
      group-data-[collapsible=offcanvas]:translate-x-0
      group-data-[side=left]:-right-4
      group-data-[side=right]:left-0
      after:absolute
      after:inset-y-0
      after:left-1/2
      after:w-[2px]
      group-data-[collapsible=offcanvas]:after:left-full in-data-[side=left]:cursor-w-resize
      in-data-[side=right]:cursor-e-resize sm:flex [[data-side=left][data-collapsible=offcanvas]_&]:-right-2
      [[data-side=left][data-state=collapsed]_&]:cursor-e-resize
      [[data-side=right][data-state=collapsed]_&]:cursor-w-resize
    `,
    separator: `
      bg-sidebar-border
      mx-2
      w-auto
    `,
    trigger: `
      size-7
    `,
  },
  variants: {
    variant: {
      floating: {},
      inset: {},
      sidebar: {
        container: `
          group-data-[side=left]:border-r-border-secondary
          group-data-[collapsible=icon]:w-(--sidebar-width-icon)
          group-data-[side=right]:border-l
        `,
        gap: `
          group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]
        `,
      },
    },
    mobile: {
      false: {
        root: `
          flex
          h-full
          w-(--sidebar-width)
          flex-col
        `,
      },
      true: {
        root: `
          w-(--sidebar-width-mobile)
        `,
      },
    },
    collapsible: {
      offcanvas: {
        gap: 'group-data-[collapsible=offcanvas]:w-0',
      },
      icon: {
        gap: 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
      },
      none: {
        gap: 'group-data-[collapsible=none]:w-0',
      },
    },
    side: {
      left: {
        container: `
          left-0
          group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]
        `,
      },
      right: {
        container: `
          right-0
          group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]
        `,
      },
    },
    showOnHover: {
      true: {
        menuAction: `
          peer-data-[active=true]/menu-button:text-sidebar-accent-foreground
          group-focus-within/menu-item:opacity-100
          group-hover/menu-item:opacity-100
          data-[state=open]:opacity-100
          md:opacity-0
          `,
      },
    },
  },
  compoundVariants: [
    {
      variant: ['floating', 'inset'],
      container: `
        p-2
        group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]
      `,
      gap: `
        group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]
      `,
    },
  ],
  defaultVariants: {
    variant: 'sidebar',
    collapsible: 'offcanvas',
    side: 'left',
  },
});

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = useState(false);

  const { wrapper } = sidebar();

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  // Adds a keyboard shortcut to toggle the sidebar.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        const target = event.target as HTMLElement | null;
        if (
          target?.isContentEditable ||
          target?.closest?.('[contenteditable="true"]') ||
          target?.tagName === 'INPUT' ||
          target?.tagName === 'TEXTAREA'
        ) {
          return;
        }
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed';

  const contextValue = useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          className={wrapper({ className })}
          data-slot="sidebar-wrapper"
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}) {
  const { root, container, gap, sidebar: sidebarClass } = sidebar();
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === 'none') {
    return (
      <div className={root({ className })} data-slot="sidebar" {...props}>
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet onOpenChange={setOpenMobile} open={openMobile} {...props}>
        <SheetContent
          className={root({ className, mobile: true })}
          data-mobile="true"
          data-sidebar="sidebar"
          data-slot="sidebar"
          side={side}
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className="group peer hidden md:block"
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-side={side}
      data-slot="sidebar"
      data-state={state}
      data-variant={variant}
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div className={gap({ collapsible, variant })} data-slot="sidebar-gap" />
      <div className={container({ side, variant })} data-slot="sidebar-container" {...props}>
        <div className={sidebarClass({ className })} data-sidebar="sidebar" data-slot="sidebar-inner">
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({ className, onClick, ...props }: React.ComponentProps<typeof Button>) {
  const { trigger } = sidebar();
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      className={trigger({ className })}
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      size="icon"
      {...props}
    >
      <FontAwesomeIcon icon={faBars} />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { sidebarRail } = sidebar();
  const { toggleSidebar } = useSidebar();

  return (
    <button
      className={sidebarRail({ className })}
      aria-label="Toggle Sidebar"
      data-sidebar="rail"
      data-slot="sidebar-rail"
      onClick={toggleSidebar}
      tabIndex={-1}
      title="Toggle Sidebar"
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  const { inset } = sidebar();

  return <main className={inset({ className })} data-slot="sidebar-inset" {...props} />;
}

function SidebarInput({ className, ...props }: React.ComponentProps<typeof Input>) {
  const { input } = sidebar();

  return <Input className={input({ className })} data-sidebar="input" data-slot="sidebar-input" {...props} />;
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  const { header } = sidebar();

  return <div className={header({ className })} data-sidebar="header" data-slot="sidebar-header" {...props} />;
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  const { footer } = sidebar();

  return <div className={footer({ className })} data-sidebar="footer" data-slot="sidebar-footer" {...props} />;
}

function SidebarSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  const { separator } = sidebar();

  return (
    <Separator className={separator({ className })} data-sidebar="separator" data-slot="sidebar-separator" {...props} />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { content } = sidebar();

  return <div className={content({ className })} data-sidebar="content" data-slot="sidebar-content" {...props} />;
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  const { group } = sidebar();

  return <div className={group({ className })} data-sidebar="group" data-slot="sidebar-group" {...props} />;
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const { groupLabel } = sidebar();
  const Comp = asChild ? SlotPrimitive.Slot : Overline;

  return (
    <Comp className={groupLabel({ className })} data-sidebar="group-label" data-slot="sidebar-group-label" {...props} />
  );
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
  const { groupAction } = sidebar();
  const Comp = asChild ? SlotPrimitive.Slot : 'button';

  return (
    <Comp
      className={groupAction({ className })}
      data-sidebar="group-action"
      data-slot="sidebar-group-action"
      {...props}
    />
  );
}

function SidebarGroupContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { groupContent } = sidebar();

  return (
    <div
      className={groupContent({ className })}
      data-sidebar="group-content"
      data-slot="sidebar-group-content"
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  const { menu } = sidebar();

  return <ul className={menu({ className })} data-sidebar="menu" data-slot="sidebar-menu" {...props} />;
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  const { menuItem } = sidebar();

  return <li className={menuItem({ className })} data-sidebar="menu-item" data-slot="sidebar-menu-item" {...props} />;
}

const sidebarMenuButtonVariants = tv({
  base: `
    peer/menu-button
    ring-sidebar-ring
    hover:bg-sidebar-accent
    hover:text-sidebar-accent-foreground
    active:bg-sidebar-accent
    active:text-sidebar-accent-foreground
    data-[active=true]:bg-sidebar-accent
    data-[active=true]:text-sidebar-accent-foreground
    data-[state=open]:hover:bg-sidebar-accent
    data-[state=open]:hover:text-sidebar-accent-foreground
    flex
    w-full
    cursor-pointer
    **:cursor-pointer
    items-center
    gap-3
    overflow-hidden
    rounded-sm
    px-3
    py-2
    text-left
    text-sm
    font-medium
    outline-hidden
    transition-all
    group-has-data-[sidebar=menu-action]/menu-item:pr-8
    group-data-[collapsible=icon]:size-10!
    group-data-[collapsible=icon]:justify-center
    group-data-[collapsible=icon]:p-2.5!
    focus-visible:ring-2
    disabled:pointer-events-none
    disabled:opacity-50
    aria-disabled:pointer-events-none
    aria-disabled:opacity-50
    data-[active=true]:font-medium
    [&>span:last-child]:truncate
    [&>svg]:size-4
    [&>svg]:shrink-0
    `,
  variants: {
    variant: {
      default: `
        text-content-secondary
        hover:bg-sidebar-accent
        hover:text-sidebar-accent-foreground
        hover:shadow-[0px_1px_4px_0px_rgba(0,0,0,0.04)]
        data-[active=true]:shadow-[0px_1px_4px_0px_rgba(0,0,0,0.04)]
      `,
      outline: `
        bg-background
        hover:bg-sidebar-accent
        hover:text-sidebar-accent-foreground
        shadow-[0_0_0_1px_hsl(var(--sidebar-border))]
        hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]
        `,
    },
    size: {
      default: 'h-11 text-sm',
      sm: 'h-7 text-xs',
      lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? SlotPrimitive.Slot : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      className={sidebarMenuButtonVariants({ variant, size, className })}
      data-active={isActive}
      data-sidebar="menu-button"
      data-size={size}
      data-slot="sidebar-menu-button"
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent align="center" hidden={state !== 'collapsed' || isMobile} side="right" {...tooltip} />
    </Tooltip>
  );
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const { menuAction } = sidebar();
  const Comp = asChild ? SlotPrimitive.Slot : 'button';

  return (
    <Comp
      className={menuAction({ className, showOnHover })}
      data-sidebar="menu-action"
      data-slot="sidebar-menu-action"
      {...props}
    />
  );
}

function SidebarMenuBadge({ className, ...props }: React.ComponentProps<'div'>) {
  const { menuBadge } = sidebar();
  return (
    <div className={menuBadge({ className })} data-sidebar="menu-badge" data-slot="sidebar-menu-badge" {...props} />
  );
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  const { menuSub } = sidebar();

  return <ul className={menuSub({ className })} data-sidebar="menu-sub" data-slot="sidebar-menu-sub" {...props} />;
}

function SidebarMenuSubItem({ className, ...props }: React.ComponentProps<'li'>) {
  const { menuSubItem } = sidebar();
  return (
    <li
      className={menuSubItem({ className })}
      data-sidebar="menu-sub-item"
      data-slot="sidebar-menu-sub-item"
      {...props}
    />
  );
}

const sidebarMenuSubButton = tv({
  base: `
    text-sidebar-foreground
    ring-sidebar-ring
    hover:bg-sidebar-accent
    hover:text-sidebar-accent-foreground
    active:bg-sidebar-accent
    active:text-sidebar-accent-foreground
    [&>svg]:text-sidebar-accent-foreground
    data-[active=true]:bg-sidebar-accent
    data-[active=true]:text-sidebar-accent-foreground
    flex
    h-7
    min-w-0
    -translate-x-px
    items-center
    gap-2
    overflow-hidden
    rounded-md
    px-2
    outline-hidden
    group-data-[collapsible=icon]:hidden
    focus-visible:ring-2
    disabled:pointer-events-none
    disabled:opacity-50
    aria-disabled:pointer-events-none
    aria-disabled:opacity-50
    [&>span:last-child]:truncate
    [&>svg]:size-4
    [&>svg]:shrink-0
  `,
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
    },
  },
});

function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}) {
  const Comp = asChild ? SlotPrimitive.Slot : 'a';

  return (
    <Comp
      className={sidebarMenuSubButton({ size, className })}
      data-active={isActive}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-slot="sidebar-menu-sub-button"
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
